import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share, PlusSquare, Smartphone } from "lucide-react";

const isPhone = () => window.matchMedia("(max-width: 767px)").matches || window.matchMedia("(pointer: coarse)").matches;
const isInstalled = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const FORCE = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("install");

// Fired by every waitlist form on success: window.dispatchEvent(new Event("lp:waitlist-joined"))
export default function InstallPrompt() {
  const [open, setOpen] = useState(false);
  const [native, setNative] = useState(() => window.__lpInstallPrompt || null);

  useEffect(() => {
    const onNative = () => setNative(window.__lpInstallPrompt);
    window.addEventListener("lp:install-available", onNative);
    const onJoined = () => {
      if (!FORCE && (!isPhone() || isInstalled() || localStorage.getItem("lp_install_prompted"))) return;
      setTimeout(() => setOpen(true), 1500);
    };
    window.addEventListener("lp:waitlist-joined", onJoined);
    if (FORCE) setTimeout(() => setOpen(true), 800);
    return () => {
      window.removeEventListener("lp:install-available", onNative);
      window.removeEventListener("lp:waitlist-joined", onJoined);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem("lp_install_prompted", String(Date.now()));
    setOpen(false);
  };

  const install = async () => {
    if (!native) return;
    native.prompt();
    await native.userChoice.catch(() => {});
    window.__lpInstallPrompt = null;
    dismiss();
  };

  const ios = isIOS();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="install-prompt"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-3 sm:inset-x-auto sm:right-6 sm:w-[380px] bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-[86] glass-panel !bg-obsidian/95 !border-signal/40 signal-glow p-5"
        >
          <button
            data-testid="install-dismiss-button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute top-1 right-1 flex items-center justify-center w-11 h-11 text-slate-400 hover:text-signal"
          >
            <X size={18} />
          </button>
          <div className="flex items-start gap-4 pr-8">
            <img src="/logo-symbol.webp" width={512} height={512} alt="" className="w-12 h-12 shrink-0 drop-shadow-[0_0_18px_rgba(63,169,245,0.5)]" />
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-signal">You're on the signal</div>
              <h3 className="mt-1 font-head font-extrabold tracking-tight text-base text-white">
                Add Launchpad TV to your home screen
              </h3>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">One tap to the launch countdown — no app store needed.</p>
            </div>
          </div>

          {native ? (
            <button
              data-testid="install-add-button"
              onClick={install}
              className="mt-4 w-full flex items-center justify-center gap-3 bg-signal text-obsidian font-bold font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3.5 hover:bg-white transition-colors duration-300"
            >
              <Smartphone size={14} />
              Add to Home Screen
            </button>
          ) : (
            <ol className="mt-4 space-y-2 font-mono text-[11px] text-slate-300" data-testid="install-manual-steps">
              <li className="flex items-center gap-3">
                <span className="text-signal">1</span>
                {ios ? <Share size={14} className="text-signal" /> : <span className="text-signal">⋮</span>}
                {ios ? "Tap Share in Safari's toolbar" : "Open your browser menu"}
              </li>
              <li className="flex items-center gap-3">
                <span className="text-signal">2</span>
                <PlusSquare size={14} className="text-signal" />
                {ios ? "Choose “Add to Home Screen”" : "Choose “Add to Home screen” or “Install app”"}
              </li>
            </ol>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
